import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from '@plato/ui/components/info-hint';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '@plato/ui/containers/dialog';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
    documentTypeKey,
    explainableMetrics,
    metricBaseKey,
    metricDefinitionKeys,
    metricDefinitionsNamespace,
    selectedFigureBaseKey,
    type ExplainableMetric,
    type MetricBase,
} from '../../../lib/metric-definition-keys.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';

type MetricCopy = {
    label: string;
    explanation: string;
};

/** Resolves a figure to its finished sentences (REQ-3777). */
function useMetricCopy(): {
    t: TFunction<typeof metricDefinitionsNamespace>;
    read: (metric: ExplainableMetric, base?: MetricBase) => MetricCopy;
} {
    const { t } = useTranslation(metricDefinitionsNamespace);
    const { orderSumDocumentTypes } = useAppConfig();

    const documentTypes = orderSumDocumentTypes.map((type) => t(documentTypeKey(type))).join(', ');
    // A list of one reads like a warning about nothing.
    const namesDocumentTypes = orderSumDocumentTypes.length > 1;

    return {
        t,
        read: (metric, base) => {
            const keys = metricDefinitionKeys(metric);
            return {
                label: t(keys.label),
                explanation: [
                    t(keys.definition, { base: t(base ? metricBaseKey(base) : selectedFigureBaseKey) }),
                    keys.documentTypes && namesDocumentTypes && t(keys.documentTypes, { documentTypes }),
                    keys.dateBasis && t(keys.dateBasis),
                ]
                    .filter(Boolean)
                    .join(' '),
            };
        },
    };
}

/** The `?` beside a figure's label. Attach it wherever a surface names one of these figures. */
export type MetricInfoHintProps = {
    metric: ExplainableMetric;
    /** What a margin figure was calculated on. */
    base?: MetricBase;
};

export function MetricInfoHint({ metric, base }: MetricInfoHintProps) {
    const { t, read } = useMetricCopy();
    const sendEvent = useAnalytics();
    const { label, explanation } = read(metric, base);

    return (
        <InfoHint
            onOpenChange={(open) => {
                if (open) {
                    sendEvent('metric_hint_opened', { metric });
                }
            }}
        >
            {/* Some of these sit inside a clickable tile, where the tap would otherwise
                reach the tile's link. ponytail: a button inside an anchor is still invalid
                nesting, so lift the title out of the tile if that starts to bite. */}
            <InfoHintTrigger
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
            />
            <InfoHintContent>
                <InfoHintHeader>
                    <InfoHintTitle>{label}</InfoHintTitle>
                </InfoHintHeader>
                <InfoHintBody className="flex flex-col gap-tight">
                    <p>{explanation}</p>
                    <p className="text-content-weaker">{t('acl_note')}</p>
                </InfoHintBody>
            </InfoHintContent>
        </InfoHint>
    );
}

/**
 * Every figure in one place. Managed by the dialog state manager rather than
 * local state: the settings menu unmounts its pop-over as soon as the dialog
 * takes the click, which would take a locally-owned dialog down with it.
 */
export const MetricExplainerDialog = DialogStateManager.create(() => {
    const { visible, remove } = useDialog();
    const { t, read } = useMetricCopy();

    return (
        <Dialog
            open={visible}
            onOpenChange={(open) => {
                if (!open) {
                    remove();
                }
            }}
        >
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>{t('title')}</DialogTitle>
                    <DialogIconClose />
                </DialogHeader>
                <DialogBody className="flex flex-col gap-relaxed">
                    <p className="text-body text-content-weaker">{t('acl_note')}</p>
                    <p className="text-body text-content-weaker">{t('erp_divergence')}</p>

                    {explainableMetrics.map((metric) => {
                        const { label, explanation } = read(metric);

                        return (
                            <div className="flex flex-col gap-tight" key={metric}>
                                <span className="text-body-emphasis">{label}</span>
                                <p className="text-body text-content-weaker">{explanation}</p>
                            </div>
                        );
                    })}
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
});
